import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText, Plus, Trash2, Eye, Save, ExternalLink, ArrowLeft, ImagePlus,
  Loader2, Tag, CheckCircle2, CircleDot, Search,
} from "lucide-react";
import { toast } from "sonner";
import DashboardShell from "../../components/shell/DashboardShell";
import api from "../../lib/api";
import { fmtUSDate as toUSDate } from "../../lib/date";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const emptyPost = () => ({
  slug: "",
  title: "",
  description: "",
  date: new Date().toISOString().slice(0, 10),
  author: "realtyvault-team",
  tags: [],
  cover_image: "",
  body: "",
  status: "draft",
});

/**
 * Admin blog editor — CRUD over markdown posts under backend/blog_posts/.
 * Two modes:
 *   - list: browse every post + "New post" button
 *   - editing: two-column layout, form on left + live preview on right
 */
export default function AdminBlogEditor() {
  const [posts, setPosts] = useState(null);
  const [authors, setAuthors] = useState([]);
  const [editing, setEditing] = useState(null); // null = list view, {} = new, {slug} = edit
  const [search, setSearch] = useState("");

  const loadPosts = useCallback(async () => {
    try {
      const r = await api.get("/admin/blog/posts");
      setPosts(r.data.posts || []);
    } catch (e) {
      toast.error("Could not load posts");
      setPosts([]);
    }
  }, []);

  useEffect(() => {
    loadPosts();
    api.get("/blog/authors").then((r) => setAuthors(r.data.authors || [])).catch(() => {});
  }, [loadPosts]);

  const openNew = () => setEditing(emptyPost());
  const openEdit = async (slug) => {
    try {
      const r = await api.get(`/admin/blog/posts/${slug}`);
      setEditing({ ...emptyPost(), ...r.data, _originalSlug: slug });
    } catch {
      toast.error("Could not load post");
    }
  };
  const closeEditor = () => setEditing(null);

  const remove = async (slug) => {
    if (!window.confirm(`Delete post "${slug}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/posts/${slug}`);
      toast.success("Post deleted");
      loadPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = useMemo(() => {
    if (!posts) return [];
    const q = search.toLowerCase().trim();
    if (!q) return posts;
    return posts.filter((p) =>
      [p.title, p.slug, p.description, ...(p.tags || [])]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q))
    );
  }, [posts, search]);

  if (editing) {
    return (
      <DashboardShell>
        <PostEditor
          post={editing}
          authors={authors}
          onClose={closeEditor}
          onSaved={() => {
            closeEditor();
            loadPosts();
          }}
        />
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <div className="space-y-6" data-testid="admin-blog-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Admin · Content</div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <FileText className="w-6 h-6 text-emerald-600" /> Blog editor
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Write, edit, and publish SEO content. Drafts are hidden from the public site, sitemap, and RSS feed.
            </p>
          </div>
          <button
            onClick={openNew}
            data-testid="admin-blog-new-post"
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white px-4 py-2 rounded-sm text-sm font-semibold transition-colors"
          >
            <Plus className="w-4 h-4" /> New post
          </button>
        </div>

        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-3 py-2">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            data-testid="admin-blog-search"
            placeholder="Filter by title, slug, or tag…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 outline-none text-sm bg-transparent placeholder:text-slate-400"
          />
          {posts && (
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
              {filtered.length} / {posts.length}
            </span>
          )}
        </div>

        {posts === null ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-sm p-10 text-center text-slate-400 text-sm">
            {posts.length === 0 ? "No posts yet. Click New post to write your first one." : "No posts match that filter."}
          </div>
        ) : (
          <ul className="space-y-2" data-testid="admin-blog-list">
            {filtered.map((p) => (
              <li
                key={p.slug}
                data-testid={`admin-blog-row-${p.slug}`}
                className="bg-white border border-slate-200 hover:border-slate-900 rounded-sm p-4 transition-colors flex items-start gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => openEdit(p.slug)}
                      className="text-sm font-bold text-slate-900 hover:text-emerald-700 text-left"
                      style={{ fontFamily: "Outfit" }}
                      data-testid={`admin-blog-edit-${p.slug}`}
                    >
                      {p.title}
                    </button>
                    {p.status === "draft" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded-sm">
                        <CircleDot className="w-2.5 h-2.5" /> Draft
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded-sm">
                        <CheckCircle2 className="w-2.5 h-2.5" /> Published
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.description || <span className="italic">No description</span>}</p>
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500 mt-2 font-mono">
                    <span>{toUSDate(p.date)}</span>
                    <span>·</span>
                    <span>{p.author || "—"}</span>
                    <span>·</span>
                    <span className="text-slate-400">/{p.slug}</span>
                    {p.tags?.length > 0 && (
                      <>
                        <span>·</span>
                        <div className="inline-flex items-center gap-1 flex-wrap">
                          {p.tags.slice(0, 4).map((t) => (
                            <span key={t} className="inline-flex items-center gap-0.5 text-[9px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-sm">
                              <Tag className="w-2 h-2" /> {t}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {p.status === "published" && (
                    <a
                      href={`/blog/${p.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 text-slate-400 hover:text-slate-900 rounded-sm hover:bg-slate-100"
                      title="View live"
                      data-testid={`admin-blog-view-${p.slug}`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    onClick={() => remove(p.slug)}
                    className="p-2 text-slate-400 hover:text-red-600 rounded-sm hover:bg-red-50"
                    title="Delete"
                    data-testid={`admin-blog-delete-${p.slug}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </DashboardShell>
  );
}

// ---------- Editor ----------

function PostEditor({ post, authors, onClose, onSaved }) {
  const isNew = !post._originalSlug;
  const [form, setForm] = useState(post);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const previewTimer = useRef(null);

  const set = (k) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: val }));
  };

  // Auto-generate slug from title while creating a new post (until user
  // manually edits the slug field)
  const [slugDirty, setSlugDirty] = useState(!isNew);
  useEffect(() => {
    if (!isNew || slugDirty || !form.title) return;
    const auto = form.title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/[\s-]+/g, "-")
      .slice(0, 120);
    setForm((f) => ({ ...f, slug: auto }));
  }, [form.title, slugDirty, isNew]);

  // Live preview — debounced 300ms
  useEffect(() => {
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try {
        const r = await api.post("/admin/blog/preview", { body: form.body });
        setPreview(r.data.html);
      } catch {
        setPreview("<p class='text-red-500'>Preview failed</p>");
      }
    }, 300);
    return () => clearTimeout(previewTimer.current);
  }, [form.body]);

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || form.tags.includes(t)) return;
    setForm((f) => ({ ...f, tags: [...f.tags, t] }));
    setTagInput("");
  };
  const removeTag = (t) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/blog/covers", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, cover_image: r.data.url }));
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async (nextStatus) => {
    const status = nextStatus || form.status;
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!form.date || !/^\d{4}-\d{2}-\d{2}$/.test(form.date)) { toast.error("Date must be YYYY-MM-DD"); return; }
    if (!form.slug) { toast.error("Slug required"); return; }
    setSaving(true);
    const payload = { ...form, status };
    // Strip meta the backend doesn't accept
    delete payload._originalSlug;
    delete payload.raw;
    delete payload.updated_at;
    try {
      if (isNew) {
        await api.post("/admin/blog/posts", payload);
        toast.success(`Post ${status === "draft" ? "saved as draft" : "published"}`);
      } else {
        await api.put(`/admin/blog/posts/${post._originalSlug}`, payload);
        toast.success(`Post ${status === "draft" ? "updated (draft)" : "updated (published)"}`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const coverPreviewUrl = form.cover_image
    ? (form.cover_image.startsWith("http") || form.cover_image.startsWith("/api/")
        ? (form.cover_image.startsWith("/api/") ? `${API_BASE}${form.cover_image}` : form.cover_image)
        : form.cover_image)
    : "";

  return (
    <div className="space-y-4" data-testid="admin-blog-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button onClick={onClose} className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900" data-testid="admin-blog-back">
            <ArrowLeft className="w-3 h-3" /> Back to all posts
          </button>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Outfit" }}>
            {isNew ? "New post" : `Editing: ${form.title || form.slug}`}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => save("draft")}
            disabled={saving}
            data-testid="admin-blog-save-draft"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:border-slate-900 text-slate-900 px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save as draft
          </button>
          <button
            onClick={() => save("published")}
            disabled={saving}
            data-testid="admin-blog-publish"
            className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Publish
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* LEFT: form */}
        <div className="space-y-3 bg-white border border-slate-200 rounded-sm p-4">
          <Field label="Title">
            <input
              data-testid="admin-blog-title"
              value={form.title}
              onChange={set("title")}
              className="w-full input-clean"
              placeholder="How to write a CRE loan renewal request that actually gets read"
            />
          </Field>
          <Field label="Slug" hint="URL path — lowercase letters, digits, hyphens only">
            <input
              data-testid="admin-blog-slug"
              value={form.slug}
              onChange={(e) => { setSlugDirty(true); set("slug")(e); }}
              className="w-full input-clean font-mono"
              placeholder="cre-loan-renewal-request"
            />
          </Field>
          <Field label="Description" hint="140-160 chars for Google search snippets">
            <textarea
              data-testid="admin-blog-description"
              rows={2}
              value={form.description}
              onChange={set("description")}
              className="w-full input-clean"
              maxLength={400}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Publish date">
              <input
                type="date"
                data-testid="admin-blog-date"
                value={form.date}
                onChange={set("date")}
                className="w-full input-clean font-mono"
              />
            </Field>
            <Field label="Author">
              <select
                data-testid="admin-blog-author"
                value={form.author}
                onChange={set("author")}
                className="w-full input-clean"
              >
                {authors.map((a) => (
                  <option key={a.slug} value={a.slug}>{a.name} — {a.role}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Tags" hint="Press Enter to add">
            <div className="flex items-center gap-2 flex-wrap border border-slate-300 rounded-sm px-2 py-1.5 focus-within:border-slate-900">
              {form.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded-sm">
                  <Tag className="w-2.5 h-2.5" /> {t}
                  <button onClick={() => removeTag(t)} className="text-slate-400 hover:text-red-600 ml-0.5" title="Remove">×</button>
                </span>
              ))}
              <input
                data-testid="admin-blog-tag-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }}
                className="flex-1 min-w-[100px] outline-none text-sm bg-transparent"
                placeholder={form.tags.length === 0 ? "e.g. sreo, refinance…" : ""}
              />
            </div>
          </Field>

          <Field label="Cover image" hint="For social share cards. Uploads land in backend/blog_posts/covers/">
            <div className="flex items-center gap-2">
              <input
                data-testid="admin-blog-cover-url"
                value={form.cover_image}
                onChange={set("cover_image")}
                className="flex-1 input-clean font-mono text-xs"
                placeholder="/marketing/foo.jpg or /api/blog/covers/…"
              />
              <label className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:border-slate-900 text-slate-900 px-3 py-2 rounded-sm text-xs font-semibold cursor-pointer whitespace-nowrap">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                Upload
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={uploadCover}
                  data-testid="admin-blog-cover-upload"
                  disabled={uploading}
                />
              </label>
            </div>
            {coverPreviewUrl && (
              <div className="mt-2 rounded-sm overflow-hidden border border-slate-200 max-w-xs">
                <img src={coverPreviewUrl} alt="Cover preview" className="w-full h-32 object-cover" data-testid="admin-blog-cover-preview" />
              </div>
            )}
          </Field>

          <Field label="Body" hint="Markdown — supports # headings, **bold**, links, code, tables">
            <textarea
              data-testid="admin-blog-body"
              rows={20}
              value={form.body}
              onChange={set("body")}
              className="w-full input-clean font-mono text-xs leading-relaxed"
              placeholder="# Your headline&#10;&#10;A short intro paragraph…"
            />
          </Field>
        </div>

        {/* RIGHT: live preview */}
        <div className="bg-white border border-slate-200 rounded-sm p-4 sticky top-4 h-fit max-h-[calc(100vh-2rem)] overflow-y-auto" data-testid="admin-blog-preview-pane">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
            <Eye className="w-4 h-4 text-slate-400" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Live preview</span>
          </div>
          <article className="prose prose-slate prose-sm max-w-none" style={{ fontFamily: "Outfit, sans-serif" }}>
            {form.title && (
              <h1 className="text-2xl font-bold tracking-tight mt-0" style={{ fontFamily: "Outfit" }}>{form.title}</h1>
            )}
            {form.description && <p className="text-sm text-slate-500 italic mt-0">{form.description}</p>}
            <div dangerouslySetInnerHTML={{ __html: preview }} />
          </article>
          {!form.title && !form.body && (
            <div className="text-slate-400 text-sm italic">Preview appears here as you type.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-600">{label}</span>
        {hint && <span className="text-[10px] text-slate-400 font-mono normal-case">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
